import type { ClientFrame, RealtimeEnvelope, RealtimeEventName, ServerFrame } from '@outfalak/shared'
import { CloseCode } from '@outfalak/shared'
import { api } from './api.js'

type Listener = (envelope: RealtimeEnvelope) => void

interface TokenResponse {
  token: string
  channels: string[]
  expiresIn: number
  url: string
}

/**
 * The dashboard's WebSocket client.
 *
 * One connection for the whole tab, shared by every screen, because a per-screen
 * socket means a merchant flicking between Orders and Wallet opens and drops
 * connections faster than the server can finish authorising them.
 *
 * Reconnection is where the real work is. The three cases behave differently on
 * purpose:
 *
 *   • Token expired mid-connection → fetch a new token and reconnect at once.
 *     The session is still good; only the two-minute subscription lapsed.
 *   • Server restarting (a deploy) → reconnect with jitter, or every dashboard
 *     on the platform stampedes the new process the instant it binds the port.
 *   • Unauthorized → stop. The session is gone, and retrying in a loop against
 *     a signed-out cookie is how you turn one expired login into a DoS.
 */
export class RealtimeClient {
  private socket: WebSocket | null = null
  private listeners = new Map<string, Set<Listener>>()
  private attempt = 0
  private stopped = false
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private statusListeners = new Set<(status: RealtimeStatus) => void>()

  private _status: RealtimeStatus = 'idle'

  get status(): RealtimeStatus {
    return this._status
  }

  connect(): void {
    this.stopped = false
    void this.open()
  }

  private setStatus(status: RealtimeStatus): void {
    if (this._status === status) return
    this._status = status
    for (const listener of this.statusListeners) listener(status)
  }

  onStatus(listener: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(listener)
    listener(this._status)
    return () => this.statusListeners.delete(listener)
  }

  /** Subscribes to one channel. Returns the unsubscribe. */
  on(channel: string, listener: Listener): () => void {
    let set = this.listeners.get(channel)
    if (!set) {
      set = new Set()
      this.listeners.set(channel, set)
    }
    set.add(listener)

    return () => {
      set!.delete(listener)
      if (set!.size === 0) this.listeners.delete(channel)
    }
  }

  /** Convenience: only fires for one event name on one channel. */
  onEvent<E extends RealtimeEventName>(
    channel: string,
    event: E,
    listener: (envelope: RealtimeEnvelope<E>) => void,
  ): () => void {
    return this.on(channel, (envelope) => {
      if (envelope.event === event) listener(envelope as RealtimeEnvelope<E>)
    })
  }

  private async open(): Promise<void> {
    if (this.stopped) return

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting')

    let credentials: TokenResponse

    try {
      credentials = await api<TokenResponse>('/v1/realtime/token', { method: 'POST' })
    } catch {
      // Could not even get a token — usually a dropped connection or a session
      // that ended. Backing off rather than hammering.
      this.scheduleReconnect()
      return
    }

    const socket = new WebSocket(credentials.url)
    this.socket = socket

    socket.addEventListener('open', () => {
      this.send({ type: 'auth', token: credentials.token })
    })

    socket.addEventListener('message', (message: MessageEvent<string>) => {
      let frame: ServerFrame
      try {
        frame = JSON.parse(message.data) as ServerFrame
      } catch {
        return
      }

      if (frame.type === 'ready') {
        this.attempt = 0
        this.setStatus('live')
        return
      }

      if (frame.type === 'event') {
        for (const listener of this.listeners.get(frame.envelope.channel) ?? []) {
          listener(frame.envelope)
        }
        return
      }
    })

    socket.addEventListener('close', (event: CloseEvent) => {
      this.socket = null

      if (this.stopped) return

      if (event.code === CloseCode.Unauthorized) {
        // The session is gone. Stop, and let the auth layer send the merchant
        // to the sign-in screen — retrying cannot fix this.
        this.setStatus('unauthorized')
        this.stopped = true
        return
      }

      if (event.code === CloseCode.TokenExpired) {
        // Still signed in; only the short-lived subscription lapsed.
        this.attempt = 0
        void this.open()
        return
      }

      this.scheduleReconnect()
    })

    socket.addEventListener('error', () => {
      // 'close' always follows, and that is where the decision is made. Handling
      // it here too would schedule two reconnects for one failure.
    })
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return

    this.setStatus('reconnecting')
    this.attempt += 1

    // Exponential with full jitter, capped at 30s. The jitter is what stops
    // every dashboard reconnecting in the same 100ms window after a deploy.
    const ceiling = Math.min(30_000, 500 * 2 ** Math.min(this.attempt, 6))
    const delay = Math.random() * ceiling

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.open()
    }, delay)
  }

  private send(frame: ClientFrame): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(frame))
  }

  disconnect(): void {
    this.stopped = true

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.socket?.close(1000, 'Client disconnected')
    this.socket = null
    this.setStatus('idle')
  }
}

export type RealtimeStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'unauthorized'

/** One client per tab. */
export const realtime = new RealtimeClient()
